import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * crypto.randomUUID() fallback for non-secure contexts (HTTP + IP).
 * Needed when testing on a real phone via local network.
 */
export function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Get avatar color based on name or index
 * Returns gradient classes for consistent avatar colors
 * @param {string} name - Name to generate color from
 * @param {number} index - Index to generate color from
 * @returns {string} Tailwind gradient classes
 */
export function getAvatarColor(name, index) {
  const colors = [
    'from-amber-600 to-amber-700',
    'from-emerald-600 to-emerald-700',
    'from-orange-600 to-orange-700',
    'from-rose-600 to-rose-700',
    'from-cyan-600 to-cyan-700',
    'from-pink-600 to-pink-700',
  ];
  
  // Se tiver nome, usar primeira letra para consistência
  if (name) {
    const charCode = name.charAt(0).toUpperCase().charCodeAt(0);
    return colors[charCode % colors.length];
  }
  
  // Se tiver índice, usar índice
  if (typeof index === 'number') {
    return colors[index % colors.length];
  }
  
  // Fallback para primeira cor
  return colors[0];
}

/**
 * Format phone number to (XXX) XXX-XXXX format or Brazilian format for +55
 * @param {string} phone - Phone number to format (can include country code)
 * @param {string} countryCode - Optional country code (e.g., "+1")
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phone, countryCode = '') {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Se o countryCode foi fornecido explicitamente, usar ele
  if (countryCode && countryCode.trim() !== '') {
    const codeDigits = countryCode.replace(/\D/g, ''); // Ex: "+55" → "55"
    
    // Se o número limpo começa com os dígitos do código do país, remover duplicação
    let numberToFormat = cleaned;
    if (cleaned.startsWith(codeDigits)) {
      numberToFormat = cleaned.substring(codeDigits.length);
    }
    
    if (countryCode === '+55') {
      // Formato brasileiro
      if (numberToFormat.length === 11) {
        // Celular: +55 (xx) xxxxx-xxxx
        const areaCode = numberToFormat.substring(0, 2);
        const firstPart = numberToFormat.substring(2, 7);
        const lastPart = numberToFormat.substring(7, 11);
        return `+55 (${areaCode}) ${firstPart}-${lastPart}`;
      } else if (numberToFormat.length === 10) {
        // Fixo: +55 (xx) xxxx-xxxx
        const areaCode = numberToFormat.substring(0, 2);
        const firstPart = numberToFormat.substring(2, 6);
        const lastPart = numberToFormat.substring(6, 10);
        return `+55 (${areaCode}) ${firstPart}-${lastPart}`;
      }
    } else if (countryCode === '+1' && numberToFormat.length === 10) {
      // Formato US/CA: +1 (xxx) xxx-xxxx
      const areaCode = numberToFormat.substring(0, 3);
      const middle = numberToFormat.substring(3, 6);
      const last = numberToFormat.substring(6, 10);
      return `+1 (${areaCode}) ${middle}-${last}`;
    }
    
    // Para outros códigos de país ou tamanhos não esperados
    return `${countryCode} ${numberToFormat}`;
  }
  
  // FALLBACK: Se o countryCode NÃO foi fornecido, tentar detectar do número
  // (mantém a lógica antiga para retrocompatibilidade)
  
  // Check if it's a Brazilian number (starts with 55 and has 12-13 digits total)
  if (cleaned.startsWith('55') && (cleaned.length === 12 || cleaned.length === 13)) {
    const number = cleaned.substring(2); // Remove country code
    
    if (number.length === 11) {
      // Celular: +55 (xx) xxxxx-xxxx
      const areaCode = number.substring(0, 2);
      const firstPart = number.substring(2, 7);
      const lastPart = number.substring(7, 11);
      return `+55 (${areaCode}) ${firstPart}-${lastPart}`;
    } else if (number.length === 10) {
      // Fixo: +55 (xx) xxxx-xxxx
      const areaCode = number.substring(0, 2);
      const firstPart = number.substring(2, 6);
      const lastPart = number.substring(6, 10);
      return `+55 (${areaCode}) ${firstPart}-${lastPart}`;
    }
  }
  
  // Extract country code if it's part of the number
  let code = countryCode;
  let number = cleaned;
  
  if (cleaned.length > 10) {
    // Assume first digits are country code
    const codeLength = cleaned.length - 10;
    code = '+' + cleaned.substring(0, codeLength);
    number = cleaned.substring(codeLength);
  }
  
  // US format for 10-digit numbers
  if (number.length === 10) {
    const areaCode = number.substring(0, 3);
    const middle = number.substring(3, 6);
    const last = number.substring(6, 10);
    return `${code ? code + ' ' : ''}(${areaCode}) ${middle}-${last}`;
  }
  
  // Return original if not matching expected formats
  return phone;
}

/**
 * Normalize phone number for comparison
 * Removes all non-numeric characters
 * @param {string} phone - Phone number to normalize
 * @returns {string} Normalized phone number (digits only)
 */
export function normalizePhone(phone) {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Remove country code from phone number
 * @param {string} phone - Full phone number (e.g., "+5527999657089")
 * @param {string} countryCode - Country code to remove (e.g., "+55")
 * @returns {string} Phone number without country code (e.g., "27999657089")
 */
export function removeCountryCode(phone, countryCode) {
  if (!phone) return '';
  
  // Normalize both to digits only
  const phoneDigits = phone.replace(/\D/g, '');
  const codeDigits = countryCode.replace(/\D/g, '');
  
  // If phone starts with the country code, remove it
  if (phoneDigits.startsWith(codeDigits)) {
    return phoneDigits.substring(codeDigits.length);
  }
  
  // Otherwise, return the phone as-is (just digits)
  return phoneDigits;
}

/**
 * Combines phone number with country code safely, avoiding duplication
 * Handles all edge cases: already complete, separated, redundant, missing
 * @param {string} phone - Phone number (may or may not include country code)
 * @param {string} countryCode - Country code (e.g., "+55", "+1")
 * @returns {string} Phone number in E.164 format (e.g., "+5527999657089")
 */
export function getFullPhoneE164(phone, countryCode) {
  if (!phone) return '';
  
  // Already starts with + = assume complete E.164 format
  if (phone.startsWith('+')) return phone;
  
  // Normalize to digits only
  const phoneDigits = phone.replace(/\D/g, '');
  const codeDigits = (countryCode || '').replace(/\D/g, '');
  
  // If phone already starts with country code digits, just add +
  if (codeDigits && phoneDigits.startsWith(codeDigits)) {
    return '+' + phoneDigits;
  }
  
  // Otherwise, prepend full country code
  return (countryCode || '') + phoneDigits;
}